import type { CSSProperties } from "react";
import { motionClass } from "@nexus/widget-kit";
import type { IpBlockingSeriesData, IpBlockingStatus } from "@nexus/widget-kit";

const STATUSES: { status: IpBlockingStatus; label: string; tone: string }[] = [
  { status: "blocked", label: "Blocked", tone: "bg-status-ok" },
  { status: "legal_redirection", label: "Legal redirection", tone: "bg-status-ok-text" },
  { status: "not_blocked", label: "Not blocked", tone: "bg-status-error" },
  { status: "other", label: "Other", tone: "bg-status-neutral" },
  { status: "not_checked", label: "Not checked", tone: "bg-border-strong" },
];

const hoursFormat = new Intl.NumberFormat("en", { maximumFractionDigits: 0 });
const shareFormat = new Intl.NumberFormat("en", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Stacked bar of approved sending hours under each prevailing PBC status. */
export function StatusHours({
  entries,
  motion,
  delay,
}: {
  entries: IpBlockingSeriesData["statusHours"];
  motion: boolean;
  delay: number;
}) {
  const hours = new Map((entries ?? []).map((entry) => [entry.status, entry.hours]));
  const total = [...hours.values()].reduce((sum, value) => sum + value, 0);
  // "Other" only appears when the source reports an unexpected status.
  const rows = STATUSES.filter(({ status }) => status !== "other" || (hours.get(status) ?? 0) > 0);
  const describe = (status: IpBlockingStatus) => {
    const value = hours.get(status) ?? 0;
    return `${hoursFormat.format(value)} h (${shareFormat.format(total ? value / total : 0)})`;
  };
  return (
    <section
      className={`${motionClass(motion, "nx-rise")} min-w-0 space-y-2 pt-2 wall-short:sr-only`}
      style={{ "--nx-delay": `${delay}ms` } as CSSProperties}
      aria-label="Hours per blocking status"
    >
      <h3 className="text-metric-caption font-medium text-secondary">Hours per blocking status</h3>
      {total > 0 ? (
        <>
          <div
            className="flex h-3 overflow-hidden rounded-full bg-border"
            role="img"
            aria-label={rows.map(({ status, label }) => `${label} ${describe(status)}`).join("; ")}
          >
            {rows.map(({ status, label, tone }) => (
              <div
                key={status}
                className={`h-full ${tone}`}
                style={{ width: `${((hours.get(status) ?? 0) / total) * 100}%` }}
                title={`${label}: ${describe(status)}`}
              />
            ))}
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-metric-detail" aria-hidden>
            {rows.map(({ status, label, tone }) => (
              <li key={status} className="flex min-w-0 items-center gap-1.5">
                <span className={`size-2 shrink-0 rounded-full ${tone}`} />
                <span className="truncate text-secondary">{label}</span>
                <span className="ml-auto font-semibold tabular-nums text-primary">
                  {shareFormat.format((hours.get(status) ?? 0) / total)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-metric-detail text-muted">No blocking hours in the period</p>
      )}
    </section>
  );
}
